// src/app/core/feature-registry.ts

export interface FeatureEntry {
  route: string;
  labelKey: string;
  /** Sidebar icon key (see NAV_ICONS). Optional — routes without one render label-only. */
  icon?: string;
}

export interface BusinessTypeConfig {
  features: FeatureEntry[];
}

export interface ModuleConfig {
  features: FeatureEntry[];
}

/**
 * Billing feature flags that control visibility of billing-related routes.
 */
export interface BillingFlags {
  quotations_enabled: boolean;
  work_orders_enabled: boolean;
  invoicing_enabled: boolean;
}

/**
 * Maps billing feature flags to the routes they control.
 * When a flag is false, its associated routes are hidden from navigation and blocked by guards.
 */
export const BILLING_FLAG_ROUTE_MAP: Record<keyof BillingFlags, string[]> = {
  quotations_enabled: ['/quotes'],
  work_orders_enabled: ['/work-orders'],
  invoicing_enabled: ['/invoices'],
};

export const BUSINESS_TYPE_REGISTRY: Record<string, BusinessTypeConfig> = {
  fixed_location: {
    features: [
      { route: '/dashboard', labelKey: 'nav.dashboard', icon: 'dashboard' },
      { route: '/resources', labelKey: 'nav.humanResources', icon: 'hr' },
      { route: '/virtual-workers', labelKey: 'nav.virtualWorkers', icon: 'ai' },
      { route: '/ai-managers', labelKey: 'nav.aiManagers', icon: 'ai' },
      { route: '/availability', labelKey: 'nav.resourceAvailability', icon: 'availability' },
      { route: '/clients', labelKey: 'nav.clients', icon: 'clients' },
      { route: '/clients/tickets', labelKey: 'nav.clientTickets', icon: 'tickets' },
      { route: '/services', labelKey: 'nav.services', icon: 'services' },
      { route: '/bookings', labelKey: 'nav.bookings', icon: 'bookings' },
      { route: '/quotes', labelKey: 'nav.quotes', icon: 'quotes' },
      { route: '/invoices', labelKey: 'nav.invoices', icon: 'invoices' },
      { route: '/work-orders', labelKey: 'nav.workOrders', icon: 'workorders' },
      { route: '/billing', labelKey: 'nav.billing', icon: 'billing' },
      { route: '/reports', labelKey: 'nav.reports' },
      { route: '/audit', labelKey: 'nav.audit' },
      { route: '/settings', labelKey: 'nav.settings', icon: 'settings' },
    ],
  },
  field_dispatch: {
    features: [
      { route: '/dashboard', labelKey: 'nav.dashboard', icon: 'dashboard' },
      { route: '/resources', labelKey: 'nav.humanResources', icon: 'hr' },
      { route: '/virtual-workers', labelKey: 'nav.virtualWorkers', icon: 'ai' },
      { route: '/ai-managers', labelKey: 'nav.aiManagers', icon: 'ai' },
      { route: '/availability', labelKey: 'nav.resourceAvailability', icon: 'availability' },
      { route: '/clients', labelKey: 'nav.clients', icon: 'clients' },
      { route: '/clients/tickets', labelKey: 'nav.clientTickets', icon: 'tickets' },
      { route: '/services', labelKey: 'nav.services', icon: 'services' },
      { route: '/bookings', labelKey: 'nav.bookings', icon: 'bookings' },
      { route: '/quotes', labelKey: 'nav.quotes', icon: 'quotes' },
      { route: '/invoices', labelKey: 'nav.invoices', icon: 'invoices' },
      { route: '/work-orders', labelKey: 'nav.workOrders', icon: 'workorders' },
      { route: '/billing', labelKey: 'nav.billing', icon: 'billing' },
      { route: '/allocation', labelKey: 'nav.allocation' },
      { route: '/reports', labelKey: 'nav.reports' },
      { route: '/audit', labelKey: 'nav.audit' },
      { route: '/settings', labelKey: 'nav.settings', icon: 'settings' },
    ],
  },
};

/**
 * Billing subscription tier identifiers.
 * The entry-level `solo_operator` tier has the most restricted feature set.
 */
export type SubscriptionTier = 'solo_operator' | 'small_business' | 'enterprise';

export const SOLO_OPERATOR_TIER: SubscriptionTier = 'solo_operator';

/**
 * Access definition for a single subscription tier. Each field is an explicit
 * allow-list of the ids/routes visible to that tier. A field left `undefined`
 * means "no restriction" (the full set is shown) for that dimension.
 *
 * This lets every plan define its own feature access separately — solo_operator
 * is the most restricted, small_business is an intermediate set, and enterprise
 * has no restrictions.
 */
export interface TierAccessConfig {
  /** Nav routes visible to the tier. `undefined` = all routes. */
  routes?: ReadonlySet<string>;
  /** AI Co-Worker (virtual worker) type names offered to the tier. `undefined` = all. */
  coworkerTypes?: ReadonlySet<string>;
  /** Settings primary tab ids visible to the tier. `undefined` = all tabs. */
  settingsTabs?: ReadonlySet<string>;
  /** System Features accordion ids visible to the tier. `undefined` = all accordions. */
  systemAccordions?: ReadonlySet<string>;
  /** Communication sub-tab ids visible to the tier. `undefined` = all sub-tabs. */
  commSubTabs?: ReadonlySet<string>;
  /**
   * Customer-chat "Custom Buttons" labels configurable by the tier (matched
   * against the button `label`). `undefined` = all buttons.
   */
  chatButtons?: ReadonlySet<string>;
}

/**
 * Enterprise-only nav routes — advanced analytics/compliance & allocation that
 * small_business does NOT receive. Everything else (including the full base
 * feature set) is available to small_business.
 */
const ENTERPRISE_ONLY_ROUTES: readonly string[] = [
  '/reports',
  '/allocation',
];

/**
 * Per-tier access configuration. This is the single source of truth for what
 * each billing plan can see. To change a plan's access, edit its entry here.
 *
 * - `solo_operator`: most restricted (6 menus, Reception only, minimal settings).
 * - `small_business`: full feature set EXCEPT the enterprise-only routes.
 * - `enterprise`: no restrictions (all fields `undefined` → everything shown).
 */
export const TIER_ACCESS_REGISTRY: Record<SubscriptionTier, TierAccessConfig> = {
  solo_operator: {
    // Menus: Dashboard, AI Co-Workers, Client, Services, Bookings, Billing, Settings.
    // Services is enabled so a solo operator can define the services their
    // Reception agent books (set up during onboarding).
    routes: new Set<string>([
      '/dashboard',
      '/virtual-workers', // AI Co-Workers
      '/clients',         // Client
      '/services',        // Services
      '/bookings',
      '/billing',
      '/settings',
      '/audit',           // reachable via Settings → System → Audit (all plans)
    ]),
    // Only "Reception" (worker type `customer_service`) is offered.
    coworkerTypes: new Set<string>(['customer_service']),
    // Only System Features + Communication tabs.
    settingsTabs: new Set<string>(['system', 'communication']),
    // Business Information + Set Time Zone + Service Boundary accordions.
    // (Solo operators are typically field-service and need the service radius.)
    systemAccordions: new Set<string>(['business-information', 'timezone', 'service-boundary']),
    // Only QR Code sub-tab.
    commSubTabs: new Set<string>(['qrcode']),
    // Only these customer-chat custom buttons (no "Need a quote" / "I am an
    // employee" — solo has no quotes/work-orders and no employees).
    chatButtons: new Set<string>(['Existing Customer', 'I am new here', 'Appointments']),
  },
  small_business: {
    // Full base feature set minus the enterprise-only routes; all other
    // dimensions unrestricted (undefined → everything shown).
    routes: undefined, // resolved dynamically below via ENTERPRISE_ONLY_ROUTES
    coworkerTypes: undefined,
    settingsTabs: undefined,
    systemAccordions: undefined,
    commSubTabs: undefined,
    chatButtons: undefined,
  },
  enterprise: {
    // No restrictions — the full feature set.
  },
};

/**
 * Resolve a billing tier string to a known SubscriptionTier, defaulting unknown
 * or missing values to `solo_operator` (the most restrictive) so a tenant with a
 * corrupt/absent tier never accidentally sees more than the entry-level plan.
 */
export function resolveTier(billingTier?: string | null): SubscriptionTier {
  if (billingTier === 'small_business' || billingTier === 'enterprise') {
    return billingTier;
  }
  return SOLO_OPERATOR_TIER;
}

/** Returns true when the resolved tier is the entry-level solo operator plan. */
export function isSoloOperator(billingTier?: string | null): boolean {
  return resolveTier(billingTier) === SOLO_OPERATOR_TIER;
}

/**
 * Returns the set of nav routes allowed for a tier, or `undefined` for no
 * route restriction. small_business is derived as "all routes except the
 * enterprise-only ones", which is computed against the caller's candidate list.
 */
function tierAllowsRoute(tier: SubscriptionTier, route: string): boolean {
  const config = TIER_ACCESS_REGISTRY[tier];
  if (config.routes) {
    return config.routes.has(route);
  }
  // small_business has no explicit route set but is denied enterprise-only routes.
  if (tier === 'small_business' && ENTERPRISE_ONLY_ROUTES.includes(route)) {
    return false;
  }
  // enterprise (and any unrestricted dimension) allows everything.
  return true;
}

/**
 * Generic helper: is a given id allowed for a tier along one access dimension?
 * `undefined` allow-list means the dimension is unrestricted for that tier.
 */
function tierAllowsId(
  billingTier: string | null | undefined,
  dimension: 'coworkerTypes' | 'settingsTabs' | 'systemAccordions' | 'commSubTabs' | 'chatButtons',
  id: string,
): boolean {
  const allowed = TIER_ACCESS_REGISTRY[resolveTier(billingTier)][dimension];
  return allowed ? allowed.has(id) : true;
}

/** Whether the given AI Co-Worker type is offered to the tenant's tier. */
export function tierAllowsCoworkerType(billingTier: string | null | undefined, typeName: string): boolean {
  return tierAllowsId(billingTier, 'coworkerTypes', typeName);
}

/** Whether the given Settings primary tab is visible to the tenant's tier. */
export function tierAllowsSettingsTab(billingTier: string | null | undefined, tabId: string): boolean {
  return tierAllowsId(billingTier, 'settingsTabs', tabId);
}

/** Whether the given System Features accordion is visible to the tenant's tier. */
export function tierAllowsSystemAccordion(billingTier: string | null | undefined, accordionId: string): boolean {
  return tierAllowsId(billingTier, 'systemAccordions', accordionId);
}

/** Whether the given Communication sub-tab is visible to the tenant's tier. */
export function tierAllowsCommSubTab(billingTier: string | null | undefined, subTabId: string): boolean {
  return tierAllowsId(billingTier, 'commSubTabs', subTabId);
}

/** Whether the given customer-chat custom button (by label) is configurable for the tenant's tier. */
export function tierAllowsChatButton(billingTier: string | null | undefined, buttonLabel: string): boolean {
  return tierAllowsId(billingTier, 'chatButtons', buttonLabel);
}

export const MODULE_REGISTRY: Record<string, ModuleConfig> = {
  // Note: quotation and invoicing routes are now in base features for both business
  // types, controlled by billing feature flags (BillingFlags) rather than active_modules.
  // Future add-on modules can be added here.
};

export const DEFAULT_FEATURES: FeatureEntry[] = [
  { route: '/dashboard', labelKey: 'nav.dashboard', icon: 'dashboard' },
  { route: '/settings', labelKey: 'nav.settings', icon: 'settings' },
];

/**
 * Computes the set of routes that should be hidden based on billing feature flags.
 * Returns an empty set when all flags are true (or not provided).
 */
export function getHiddenBillingRoutes(billingFlags?: Partial<BillingFlags>): Set<string> {
  const hidden = new Set<string>();
  if (!billingFlags) {
    return hidden;
  }
  for (const [flag, routes] of Object.entries(BILLING_FLAG_ROUTE_MAP)) {
    const flagValue = billingFlags[flag as keyof BillingFlags];
    if (flagValue === false) {
      for (const route of routes) {
        hidden.add(route);
      }
    }
  }
  return hidden;
}

export function resolveFeatures(
  businessType: string | undefined | null,
  activeModules: string[],
  billingFlags?: Partial<BillingFlags>,
  billingTier?: string | null
): FeatureEntry[] {
  const key = businessType ?? '';
  if (!Object.prototype.hasOwnProperty.call(BUSINESS_TYPE_REGISTRY, key)) {
    return DEFAULT_FEATURES;
  }
  const config = BUSINESS_TYPE_REGISTRY[key];

  const baseFeatures = config.features;
  const moduleFeatures: FeatureEntry[] = [];

  for (const moduleId of activeModules) {
    if (Object.prototype.hasOwnProperty.call(MODULE_REGISTRY, moduleId)) {
      const moduleConfig = MODULE_REGISTRY[moduleId];
      moduleFeatures.push(...moduleConfig.features);
    }
    // Unrecognized modules are silently ignored
  }

  let combined: FeatureEntry[];
  if (moduleFeatures.length === 0) {
    combined = baseFeatures;
  } else {
    // Insert module features after base items but before settings
    const settingsIndex = baseFeatures.findIndex(f => f.route === '/settings');
    const beforeSettings = baseFeatures.slice(0, settingsIndex);
    const settingsAndAfter = baseFeatures.slice(settingsIndex);
    combined = [...beforeSettings, ...moduleFeatures, ...settingsAndAfter];
  }

  // Filter out routes hidden by billing feature flags
  const hiddenRoutes = getHiddenBillingRoutes(billingFlags);
  let filtered = hiddenRoutes.size === 0
    ? combined
    : combined.filter(f => !hiddenRoutes.has(f.route));

  // Apply per-tier route allow-list ONLY when a billing tier is explicitly
  // provided. Each tier defines its own visible menu: solo_operator (most
  // restricted) < small_business (all but enterprise-only) < enterprise
  // (everything). When no tier is passed, tier gating is skipped entirely so
  // callers that don't supply a tier get the unfiltered business-type set.
  if (billingTier !== undefined && billingTier !== null) {
    const tier = resolveTier(billingTier);
    filtered = filtered.filter(f => tierAllowsRoute(tier, f.route));
  }

  return filtered;
}

export function resolveAllowedRoutes(
  businessType: string | undefined | null,
  activeModules: string[],
  billingFlags?: Partial<BillingFlags>,
  billingTier?: string | null
): Set<string> {
  const features = resolveFeatures(businessType, activeModules, billingFlags, billingTier);
  return new Set(features.map(f => f.route));
}
